import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBook, FiUsers, FiGlobe, FiPlay, FiCheck, FiChevronDown, FiArrowRight, FiTarget, FiList, FiCalendar, FiClock } = FiIcons;

const Syllabus = ({ updateProgress, setCurrentSection }) => {
  const [activeModule, setActiveModule] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentSection('syllabus');
    // Optional: You might want to track looking at the syllabus as progress
    // updateProgress('syllabus'); 
  }, []);

  const syllabusData = [
    {
      id: 1,
      title: "Nouns & Gender Foundations",
      description: "Establish the core building blocks of Amharic sentences with a focus on gendered language.",
      duration: "Week 1",
      path: "/nouns",
      icon: FiBook,
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      topics: [
        "Biological vs. Grammatical Gender",
        "Definiteness: The -u and -wa suffixes",
        "Pluralization: The -och suffix",
        "Irregular Plurals (Broken Plurals)",
        "Common Household Nouns"
      ]
    },
    {
      id: 2,
      title: "The Social Hierarchy of Pronouns",
      description: "Learn how to navigate complex social relationships and hierarchy through pronoun choice.",
      duration: "Week 2",
      path: "/pronouns",
      icon: FiUsers,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      topics: [
        "Independent Pronouns (I, You, He, She...)",
        "Possessive Suffixes & Ownership",
        "The Formal 'You' (Erswo) for Elders",
        "Honorifics in Third Person references",
        "Distinguishing Casual vs. Formal Address"
      ]
    },
    {
      id: 3,
      title: "Cultural Context & Application",
      description: "Apply grammatical rules to real-world Ethiopian scenarios and understand the 'why' behind the rules.",
      duration: "Week 3",
      path: "/cultural",
      icon: FiGlobe,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      topics: [
        "The Collective 'We' (Egna) in Family",
        "Religious Protocol & Address",
        "Mediation (Shimgilina) Speech Patterns",
        "Marketplace & Commercial Politeness",
        "Hospitality & Guest Greetings"
      ]
    },
    {
      id: 4,
      title: "Fluency & Practice",
      description: "Solidify your knowledge through interactive exercises, audio drills, and self-reflection.",
      duration: "Week 4",
      path: "/exercises",
      icon: FiPlay,
      color: "text-rose-600",
      bgColor: "bg-rose-50",
      borderColor: "border-rose-200",
      topics: [
        "Audio Pronunciation Mastery",
        "Role-Playing Social Scenarios",
        "Listening Comprehension Challenges",
        "Final Reflection & Assessment",
        "Course Certification"
      ]
    }
  ];

  return (
    <PageLayout
      title="Course Syllabus"
      subtitle="Your comprehensive roadmap to mastering Amharic Social Grammar. Track your journey week by week."
      icon={FiList}
      color="text-indigo-600"
      bgColor="bg-indigo-50"
      prevLink="/"
      prevLabel="Dashboard"
      nextLink="/nouns"
      nextLabel="Start Module 1"
    >
      <div className="space-y-8 fade-in">
        {/* Course Overview Card */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-4">Course Overview</h2>
              <p className="text-indigo-100 text-lg leading-relaxed mb-6">
                This course is designed to take you from basic grammar to social fluency. By understanding the cultural context behind the rules, you'll learn not just to speak Amharic, but to communicate with respect and belonging.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <SafeIcon icon={FiCalendar} />
                  <span>4 Weeks</span>
                </div>
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <SafeIcon icon={FiClock} />
                  <span>2-3 Hours/Week</span>
                </div>
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <SafeIcon icon={FiTarget} />
                  <span>Beginner Friendly</span>
                </div>
              </div>
            </div>
            
            {/* Circular Progress (Visual Only for Syllabus context) */}
            <div className="relative w-32 h-32 flex-shrink-0">
               <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  className="text-indigo-800"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="transparent"
                  strokeDasharray={351.86}
                  strokeDashoffset={351.86 * 0.25} // 75% filled visual
                  className="text-white"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className="text-2xl font-bold">4</span>
                <span className="text-xs uppercase opacity-80">Modules</span>
              </div>
            </div>
          </div>
        </div>

        {/* Syllabus Modules */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiList} className="text-indigo-600" />
            Detailed Curriculum
          </h3>
          
          <div className="space-y-4">
            {syllabusData.map((module, index) => (
              <div 
                key={index}
                className={`rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                  activeModule === index 
                  ? `border-indigo-500 shadow-lg bg-white` 
                  : 'border-gray-100 bg-white hover:border-indigo-200 hover:shadow-md'
                }`}
              >
                <button 
                  onClick={() => setActiveModule(activeModule === index ? -1 : index)}
                  className="w-full flex items-start gap-5 p-6 text-left"
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl shadow-sm flex-shrink-0 transition-transform duration-300 ${activeModule === index ? 'scale-110' : ''} ${module.bgColor} ${module.color}`}>
                    <SafeIcon icon={module.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${module.bgColor} ${module.color}`}>
                        {module.duration}
                      </span>
                      <SafeIcon 
                        icon={FiChevronDown} 
                        className={`text-gray-400 transition-transform duration-300 ${activeModule === index ? 'rotate-180 text-indigo-500' : ''}`} 
                      />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1">{module.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 md:line-clamp-none">{module.description}</p>
                  </div>
                </button>
                
                {/* Expanded Topics */}
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    activeModule === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 pt-0 md:ml-[5.5rem]">
                    <div className="h-px w-full bg-gray-100 mb-6"></div>
                    
                    <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 mb-8">
                      {module.topics.map((topic, tIdx) => (
                        <div key={tIdx} className="flex items-start gap-3 text-sm text-gray-700">
                          <div className={`w-5 h-5 rounded-full ${module.bgColor} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <SafeIcon icon={FiCheck} className={`${module.color} text-xs`} />
                          </div>
                          <span>{topic}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end">
                      <Link 
                        to={module.path}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white shadow-lg transition-all transform hover:-translate-y-1 hover:shadow-xl bg-gradient-to-r ${
                          module.color.includes('green') ? 'from-green-500 to-emerald-600' :
                          module.color.includes('blue') ? 'from-blue-500 to-cyan-600' :
                          module.color.includes('amber') ? 'from-amber-500 to-orange-600' :
                          'from-rose-500 to-pink-600'
                        }`}
                      >
                        Start Learning Module
                        <SafeIcon icon={FiArrowRight} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Syllabus;